import React, { useEffect, useRef, useState } from "react";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { Icon } from "./brand";
import { api } from "../api";
import MfaEnrollWizard from "./MfaEnrollWizard";
import {
  shouldOfferPasskeySetup, recordDecline, markDeviceHasPasskey, takePasskeySetupIntent,
  platformAuthenticatorAvailable, deviceHasPasskey,
} from "../utils/passkeyDevice";

// Gentle, dismissible nudge to set up Face ID / passkey sign-in. Shows only when
// the browser supports passkeys, the user hasn't enrolled one yet (confirmed
// server-side), it's at least their 2nd login on this device, and they haven't
// snoozed it. Dismissing backs off with an escalating snooze (see
// passkeyDevice.ts) so we never nag. This is a convenience upsell — it does NOT
// replace the required-MFA enrollment gate in App.tsx.
export default function PasskeyUpsell() {
  const [show, setShow] = useState(false);
  const [wizard, setWizard] = useState(false);
  // True when the account already has a passkey enrolled on SOME device —
  // just not this one (different browser/OS sync ecosystem: Windows Hello,
  // iCloud Keychain, Google Password Manager don't reach each other). Swaps
  // the copy to "add it here too" instead of a generic first-time pitch.
  const [elsewhere, setElsewhere] = useState(false);
  // True when we opened setup because the user explicitly asked for it on the
  // login screen (queued intent) — so cancelling the wizard closes the whole
  // thing instead of dropping back to a "maybe later" card.
  const intentRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let supported = false;
    try { supported = browserSupportsWebAuthn(); } catch {}
    if (!supported) return;

    (async () => {
      // Don't nag with an offer this device literally cannot fulfill —
      // registration needs a platform authenticator (Face ID/Touch ID/
      // Windows Hello), not just the WebAuthn API. See platformAuthenticatorAvailable.
      const pkReady = await platformAuthenticatorAvailable();
      if (cancelled || !pkReady) return;

      // The user tapped "Sign in with Face ID" but had none enrolled on this
      // device → go straight into setup now (regardless of the gentle-nudge
      // gating, and even if they have a passkey on another device).
      if (takePasskeySetupIntent()) {
        // The intent flag can outlive the moment it was queued for — e.g. it's
        // set on the login screen, but PasskeyUpsell doesn't mount at all while
        // App.tsx's mandatory MFA-enrollment gate is showing, so if the user
        // completes passkey setup THERE (or via any other route) before this
        // component ever mounts, the flag is still sitting unconsumed and gets
        // replayed here on a later login — re-forcing the wizard for a passkey
        // that already exists on this device. Confirmed on a real client
        // 2026-09-04: login screen (no local passkey, queued intent) → required
        // MFA wizard → created passkey there → logged out → logged back in with
        // the new passkey → got the "create a passkey" wizard AGAIN. Bail (after
        // consuming the flag above) if this device is already covered.
        if (deviceHasPasskey()) return;
        intentRef.current = true;
        setShow(true);
        setWizard(true);
        return;
      }

      if (!shouldOfferPasskeySetup()) return;
      try {
        const s = await api.get("/auth/mfa/status");
        // An account-wide passkey (enrolled on some OTHER device) does NOT
        // mean this device has one — passkeys don't sync across ecosystems.
        // Previously this branch called markDeviceHasPasskey() here and bailed,
        // which (a) wrongly flagged THIS device as passkey-ready so Login.tsx's
        // silent on-open auto-prompt fired a WebAuthn ceremony for a credential
        // that doesn't exist locally — surfacing a confusing cross-device QR
        // flow with zero explanation, the exact thing that stranded a client —
        // and (b) skipped ever offering to add a passkey HERE, so multi-device
        // coverage never grew. Still gate on shouldOfferPasskeySetup() (which
        // already checks the real per-device flag), just don't fabricate it.
        if (!cancelled) {
          setElsewhere((s?.methods || []).includes("passkey"));
          setShow(true);
        }
      } catch { /* status unavailable — don't nag */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => { recordDecline(); setShow(false); };

  if (!show) return null;

  return (
    <>
      <div style={card} role="dialog" aria-label="Set up faster sign-in">
        <button onClick={dismiss} aria-label="Dismiss" style={closeBtn}>×</button>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div style={iconWrap} aria-hidden><Icon name="lock" size={24} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ margin: "0 0 4px", fontSize: "14px", fontWeight: 700, color: "#111827" }}>
              {elsewhere ? "Add a passkey on this device too" : "Skip the password — sign in with a passkey"}
            </h4>
            <p style={{ margin: "0 0 12px", fontSize: "12.5px", color: "#6B7280", lineHeight: 1.5 }}>
              {elsewhere
                ? "Your passkey on another device doesn't carry over to this one — set one up here too so you can skip the password everywhere you sign in. Takes 10 seconds."
                : "Set up a passkey and next time just open the app and unlock with your face or fingerprint. No password, no authenticator code. Takes 10 seconds."}
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setWizard(true)} style={primaryBtn}>Set up passkey</button>
              <button onClick={dismiss} style={laterBtn}>Maybe later</button>
            </div>
          </div>
        </div>
      </div>

      {wizard && (
        <MfaEnrollWizard
          forceMethod="passkey"
          onClose={() => { setWizard(false); if (intentRef.current) setShow(false); }}
          onComplete={() => { markDeviceHasPasskey(); setWizard(false); setShow(false); }}
        />
      )}
    </>
  );
}

const card: React.CSSProperties = {
  position: "fixed", right: "20px", bottom: "20px", zIndex: 9000,
  width: "min(360px, calc(100vw - 40px))",
  background: "white", border: "1px solid #E5E7EB", borderRadius: "4px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.14)", padding: "16px 18px",
  fontFamily: "inherit",
};
const iconWrap: React.CSSProperties = {
  width: "36px", height: "36px", borderRadius: "4px", flexShrink: 0,
  background: "var(--brand-accent-10, #EEF2FF)", display: "flex",
  alignItems: "center", justifyContent: "center", fontSize: "18px",
};
const closeBtn: React.CSSProperties = {
  position: "absolute", top: "8px", right: "10px", background: "transparent",
  border: "none", cursor: "pointer", fontSize: "18px", color: "#9CA3AF", lineHeight: 1, padding: "2px 4px",
};
const primaryBtn: React.CSSProperties = {
  padding: "8px 14px", background: "var(--brand-primary)", color: "white", border: "none",
  borderRadius: "4px", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
};
const laterBtn: React.CSSProperties = {
  padding: "8px 12px", background: "white", color: "#6B7280", border: "1px solid #E5E7EB",
  borderRadius: "4px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
};
